import React from 'react';

/**
 * A one-node channel from a page to the navbar's title row.
 *
 * The navbar's title comes from the route table — it is a property of *where*
 * you are, and that is right for every screen in the app. The template editor
 * needs one thing more: the version its title refers to. "Operation template"
 * with no version beside it is ambiguous the moment a draft exists, and the
 * answer is page state (`v4 draft`), not route state, so the route table cannot
 * supply it.
 *
 * A context rather than a portal because the navbar is not always mounted — the
 * login screen renders outside the shell — and a portal into a missing node is
 * a runtime error rather than a no-op. A page that sets an accessory outside the
 * shell simply has nowhere for it to land, which is the correct outcome.
 *
 * **One slot, last writer wins.** Two pages are never mounted at once, and a
 * stack would only make the cleanup order matter.
 */
type NavbarSlotValue = {
    accessory: React.ReactNode;
    setAccessory: (node: React.ReactNode) => void;
};

const NavbarSlotContext = React.createContext<NavbarSlotValue>({
    accessory: null,
    // Outside the shell there is no navbar to fill, so setting is a no-op
    // rather than a throw: `useNavbarAccessory` is called unconditionally by
    // the pages that have one, including in tests that render them bare.
    setAccessory: () => {},
});

export const NavbarSlotProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [accessory, setAccessory] = React.useState<React.ReactNode>(null);
    const value = React.useMemo(
        () => ({ accessory, setAccessory }),
        [accessory],
    );

    return (
        <NavbarSlotContext.Provider value={value}>
            {children}
        </NavbarSlotContext.Provider>
    );
};

/** What the navbar renders after its title. `null` on every other screen. */
export const useNavbarSlot = (): React.ReactNode =>
    React.useContext(NavbarSlotContext).accessory;

/**
 * Put a node beside the navbar title for as long as this page is mounted.
 *
 * Clears on unmount, so navigating away cannot leave a stale chip describing a
 * screen that is no longer open — the failure mode that would make this worse
 * than no chip at all.
 *
 * The caller is responsible for the node's identity: pass a `useMemo`'d element
 * or a stable one, or this re-sets state on every render.
 */
export const useNavbarAccessory = (node: React.ReactNode): void => {
    const { setAccessory } = React.useContext(NavbarSlotContext);

    React.useEffect(() => {
        setAccessory(node);
        return () => setAccessory(null);
    }, [node, setAccessory]);
};
